import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, CircleCheckIcon, CloudRainIcon, CreditCardIcon, DownloadIcon, FileTextIcon, GraduationCapIcon, HeartHandshakeIcon, LockKeyholeIcon, MailIcon, PencilIcon, ShieldCheckIcon, SparklesIcon, StarIcon, TimerIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { MetaPixel } from '../components/MetaPixel';
import { PhoneVideoPlayer } from '../components/PhoneVideoPlayer';
import { Reveal } from '../components/Reveal';
import { SectionHeading } from '../components/SectionHeading';
const tomes = [{
  number: 'TOMO 1',
  title: '30 Juegos Sin Materiales',
  copy: 'Para cuando no hay nada preparado y necesitás resolver ahora mismo. Solo el cuerpo, la voz y la imaginación.',
  icon: SparklesIcon,
  color: 'bg-[#007C72]'
}, {
  number: 'TOMO 2',
  title: '30 Juegos Con Hojas y Lápices',
  copy: 'Actividades creativas usando solo lo que ya tienen sobre el banco. Sin buscar nada más.',
  icon: PencilIcon,
  color: 'bg-[#58B9D5]'
}, {
  number: 'TOMO 3',
  title: '30 Juegos de Movimiento Dentro del Aula',
  copy: 'Para que se muevan de forma segura, cooperativa y controlada sin salir al patio.',
  icon: ZapIcon,
  color: 'bg-[#F47B20]'
}, {
  number: 'TOMO 4',
  title: '30 Juegos Para Volver a la Calma',
  copy: 'Para después del recreo, educación física o cuando el grupo necesita bajar la energía.',
  icon: HeartHandshakeIcon,
  color: 'bg-[#C91458]'
}, {
  number: 'TOMO 5',
  title: '30 Juegos Rápidos de 5 Minutos',
  copy: 'Para esos momentos sueltos donde quedan pocos minutos y necesitás un corte rápido y efectivo.',
  icon: TimerIcon,
  color: 'bg-[#7946A8]'
}];
const needIndex = [['🔴', 'Muy inquieto o agitado', 'Tomo 4: Volver a la calma'], ['🟡', 'Necesitando moverse sin salir', 'Tomo 3: Movimiento dentro del aula'], ['🟢', 'Sin materiales disponibles', 'Tomo 1: Sin materiales'], ['⚡', 'Con pocos minutos disponibles', 'Tomo 5: Juegos de 5 minutos'], ['✏️', 'Con hojas y lápices sobre el banco', 'Tomo 2: Hojas y lápices'], ['🌧️', 'Día de lluvia completo', 'Tomos 1 y 3 combinados'], ['😤', 'Después del recreo alterado', 'Tomo 4 + Bono de calma'], ['🕐', 'Al cierre de la jornada', 'Tomo 4 o Tomo 5']];
const testimonials = [{
  quote: 'Antes cuando llovía no sabía qué hacer. Ahora abro el PDF en el celular, elijo un juego y empiezo. El grupo cambió completamente.',
  name: 'Maestra Laura M.',
  role: 'Educación Primaria — Montevideo'
}, {
  quote: 'Lo descargué en el celular y lo tengo siempre a mano. Lo que más me gustó es que los juegos son cooperativos. Nadie pierde, nadie queda afuera.',
  name: 'Maestra Valentina R.',
  role: 'Jardín de Infantes — Colonia del Sacramento'
}, {
  quote: 'Ahorro tiempo y energía cada semana. Ya no improviso ni busco en internet. Tengo todo organizado y listo en un solo PDF.',
  name: 'Maestra Gabriela S.',
  role: 'Educación Inicial — Montevideo'
}, {
  quote: 'El bono de actividades de calma me salvó después del recreo. Lo uso todos los días y el grupo ya reconoce la señal.',
  name: 'Maestra Romina P.',
  role: 'Primer Ciclo — Rivera'
}, {
  quote: 'Pensé que era otro libro más de juegos. No es así. Está pensado para el aula real, con grupos reales y el tiempo real que tenemos las maestras.',
  name: 'Maestra Cecilia T.',
  role: 'Educación Primaria — Salto'
}];
const faqs = [['¿Cómo se realiza el pago?', 'El pago se realiza de forma 100% segura a través de Mercado Pago. Podés pagar con tarjeta de crédito, tarjeta de débito o saldo disponible en tu cuenta. Tu información financiera está protegida en todo momento.'], ['¿Cómo recibo el material después de la compra?', 'Inmediatamente después de confirmar tu pago recibís un email automático con los enlaces de descarga de tus 5 tomos en PDF y el bono. El acceso es inmediato, las 24 horas.'], ['¿En qué formato vienen los materiales?', 'Todo el material es 100% digital en formato PDF. No hay nada físico, no hay envíos y no necesitás imprimir nada para usarlo.'], ['¿Desde qué dispositivos puedo verlos?', 'Desde cualquier dispositivo: celular, tablet o computadora. Una vez descargados no necesitás internet para abrirlos ni usarlos.'], ['¿Para qué edades están pensados los juegos?', 'Para niños de 4 a 12 años. Cada juego incluye la edad recomendada y variantes para adaptarlo a distintos grupos y niveles.'], ['¿Puedo usarlos con varias clases?', 'Sí. Una sola compra te da acceso completo a los 5 tomos y el bono. Los usás con todas tus clases sin restricciones ni costos adicionales.'], ['¿Los juegos son competitivos?', 'No. Los 150 juegos son 100% cooperativos. El objetivo es que todo el grupo logre algo juntos. Nadie pierde y nadie queda afuera.'], ['¿Cómo funciona la garantía de 7 días?', 'Tenés 7 días desde que recibís tus PDFs para probarlos en el aula. Si por cualquier motivo no te convencen, escribinos y te devolvemos el 100% de tu dinero a través de Mercado Pago sin preguntas.'], ['¿Dónde puedo ver más recursos de Aula de Éxitos?', 'En www.tufenixdigital.store encontrás todos nuestros materiales digitales para maestras y educadoras.']];
function PurchaseButton({
  children
}: {
  children: React.ReactNode;
}) {
  return <a href="https://tufenixdigital.store/cart/54012215099756:1" className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3.5 text-center text-sm font-black text-emerald-950 shadow-lg shadow-amber-950/20 transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-amber-200 sm:w-auto sm:px-7" data-id="element-38">
      {children} <ArrowRightIcon size={18} data-id="element-39" />
    </a>;
}
function PaymentNote() {
  return <p className="mt-4 text-xs font-medium leading-5 text-emerald-50/80" data-id="element-40">
      💳 Pago seguro a través de Mercado Pago · 📄 100% digital en PDF · ⚡
      Recibís todo por email al instante
    </p>;
}
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <>
      <MetaPixel data-id="element-41" />
      <main className="w-full overflow-hidden bg-[#f7fcf9] text-emerald-950" data-id="element-42">
        <div className="sticky top-0 z-30 bg-amber-400 px-4 py-2 text-center text-[11px] font-extrabold text-emerald-950" data-id="element-43">
          🌐 Descubrí todos nuestros recursos para el aula en →{' '}
          <a className="underline underline-offset-2" href="https://www.tufenixdigital.store" data-id="element-44">
            www.tufenixdigital.store
          </a>
        </div>

        <section className="bg-emerald-950 px-5 pb-16 pt-8 text-white sm:px-8 lg:pb-24" data-id="element-45">
          <nav className="mx-auto flex max-w-6xl items-center justify-between" data-id="element-46">
            <a href="#" className="text-lg font-black tracking-tight sm:text-xl" data-id="element-47">
              Aula de <span className="text-amber-400" data-id="element-48">Éxitos</span>
            </a>
            <a href="#comprar" className="text-xs font-bold text-emerald-100 hover:text-amber-300" data-id="element-49">
              Ver el pack
            </a>
          </nav>
          <div className="mx-auto grid max-w-6xl items-center gap-12 pt-12 lg:grid-cols-[1.1fr_.9fr]" data-id="element-50">
            <Reveal data-id="element-51">
              <h1 className="mt-5 max-w-2xl text-4xl font-black leading-[.98] tracking-tight sm:text-5xl lg:text-6xl" data-id="element-52">
                Cuando el grupo pregunta
                <br data-id="element-53" />
                “¿A qué jugamos?”
                <br data-id="element-54" />
                <span className="text-amber-400" data-id="element-55">
                  Vos ya tenés la respuesta.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-emerald-50/80" data-id="element-56">
                Actividades cooperativas para maestras de Educación Inicial y
                Primaria que necesitan resolver en este momento, sin preparar
                nada, sin imprimir nada y sin materiales especiales.
              </p>
              <div className="mt-7" data-id="element-57">
                <PurchaseButton data-id="element-58">
                  QUIERO MI PACK LISTO PARA USAR AHORA
                </PurchaseButton>
              </div>
              <PaymentNote data-id="element-59" />
            </Reveal>

            <Reveal delay={0.1} className="mx-auto w-full max-w-md" data-id="element-60">
              <div className="rounded-[2rem] border-[5px] border-emerald-100 bg-white p-2 shadow-2xl shadow-black/30" data-id="element-61">
                <div className="relative overflow-hidden rounded-[1.4rem] bg-emerald-900" data-id="element-62">
                  <img src="https://cdn.magicpatterns.com/uploads/igJ7WUFBHEZMToQa4oHAeG/ChatGPT_Image_10_jul_2026,_02_08_04_p.m..png" alt="Mockup del pack digital ¡Maestra… ¿A Qué Jugamos? de Aula de Éxitos" width="1024" height="1024" fetchPriority="high" decoding="async" className="aspect-square w-full object-cover" data-id="element-63" />
                  <span className="absolute left-3 top-3 rounded-full bg-emerald-950/90 px-3 py-1 text-[9px] font-black tracking-widest text-white" data-id="element-64">
                    COLECCIÓN DIGITAL
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-emerald-50 p-3 text-center text-emerald-950" data-id="element-65">
                  <div data-id="element-66">
                    <strong className="block text-lg leading-none" data-id="element-67">150</strong>
                    <span className="text-[9px] font-bold" data-id="element-68">juegos</span>
                  </div>
                  <div data-id="element-69">
                    <strong className="block text-lg leading-none" data-id="element-70">5</strong>
                    <span className="text-[9px] font-bold" data-id="element-71">tomos</span>
                  </div>
                  <div data-id="element-72">
                    <strong className="block text-lg leading-none" data-id="element-73">4.9★</strong>
                    <span className="text-[9px] font-bold" data-id="element-74">valoración</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-75">
          <Reveal data-id="element-76">
            <SectionHeading eyebrow="¿ALGUNA VEZ TE HA PASADO ESTO?" title="Ser maestra hoy significa resolver imprevistos todo el tiempo." data-id="element-77" />
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-3" data-id="element-78">
            {[[TimerIcon, 'Sin tiempo para preparar', 'Dedicás horas pensando qué hacer y cuando llegás al aula el grupo no responde como esperabas.'], [UsersIcon, 'El grupo fuera de control', 'Volvieron del recreo corriendo, hay ruido por todos lados y necesitás una solución ahora mismo.'], [CloudRainIcon, 'El recreo cancelado', 'Llueve, no pueden salir y tenés veinte niños mirándote esperando que hagas algo.']].map(([Icon, title, copy], index) => {
            const CardIcon = Icon as typeof TimerIcon;
            return <Reveal key={String(title)} delay={index * 0.05} data-id="element-79">
                  <article className="h-full rounded-xl border border-emerald-100 bg-white p-6 shadow-sm" data-id="element-80">
                    <CardIcon className="h-9 w-9 rounded-lg bg-rose-100 p-2 text-rose-600" data-id="element-81" />
                    <h3 className="mt-5 text-lg font-black" data-id="element-82">{String(title)}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-83">
                      {String(copy)}
                    </p>
                  </article>
                </Reveal>;
          })}
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8" data-id="element-84">
          <div className="mx-auto max-w-4xl" data-id="element-85">
            <Reveal data-id="element-86">
              <SectionHeading eyebrow="Y LO PEOR ES ESTO" title="Cada vez que pasa eso, terminás sintiéndote así:" data-id="element-87" />
            </Reveal>
            <Reveal delay={0.06} data-id="element-88">
              <ul className="mt-10 space-y-4 rounded-2xl bg-rose-50 p-6 sm:p-8" data-id="element-89">
                {['Buscás actividades en internet y no encontrás nada concreto ni organizado.', 'Improvisás algo que no funciona y el grupo se desordena todavía más.', 'Llegás a casa agotada después de haber apagado incendios todo el día.', 'Sabés que hay una mejor manera de manejar esos momentos pero no tenés el recurso a mano.', 'Sentís que el tiempo libre del grupo siempre termina en caos o en conflicto.'].map(item => <li className="flex gap-3 text-sm font-semibold leading-6 text-rose-950" key={item} data-id="element-90">
                    <span className="text-rose-500" data-id="element-91">✕</span>
                    {item}
                  </li>)}
              </ul>
              <p className="mt-9 text-center text-2xl font-black leading-tight text-emerald-950" data-id="element-92">
                ¿Y si pudieras tener la respuesta lista antes de que llegue la
                pregunta?
              </p>
              <div className="mt-7 text-center" data-id="element-93">
                <PurchaseButton data-id="element-94">QUIERO RESOLVER ESTO HOY</PurchaseButton>
                <p className="mt-4 text-xs text-slate-500" data-id="element-95">
                  💳 Pago seguro · 📄 PDFs al instante · 🛡️ Garantía de 7 días
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-96">
          <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-97">
            <Reveal className="order-2 lg:order-1" data-id="element-98">
              <p className="eyebrow" data-id="element-99">LA SOLUCIÓN</p>
              <h2 className="mt-3 text-4xl font-black leading-[1.02] tracking-tight" data-id="element-100">
                Maestra… ¿A Qué Jugamos?
              </h2>
              <p className="mt-5 text-base leading-7 text-slate-600" data-id="element-101">
                5 tomos 100% digitales en PDF con 150 juegos cooperativos para
                días de lluvia, recreos bajo techo y momentos libres.
              </p>
              <p className="mt-4 text-base leading-7 text-slate-600" data-id="element-102">
                Un material que descargás en segundos, guardás en tu dispositivo
                y usás cuando lo necesitás. Sin esperar. Sin envíos. Sin
                imprimir nada.
              </p>
              <h3 className="mt-7 text-xl font-black" data-id="element-103">¿Por qué funciona?</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-104">
                Porque no es un libro para leer de principio a fin. Es un
                recurso para abrir, elegir y empezar en segundos. Te dejamos un
                ejemplo.
              </p>
            </Reveal>
            <Reveal delay={0.08} className="order-1 mx-auto lg:order-2" data-id="element-105">
              <PhoneVideoPlayer data-id="element-106" />
            </Reveal>
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8" data-id="element-107">
          <Reveal data-id="element-108">
            <SectionHeading eyebrow="LO QUE RECIBÍS" title={<>
                  5 tomos en PDF -150 juegos
                  <br data-id="element-110" />
                  Una solución para cada momento.
                </>} data-id="element-109" />
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-111">
            {tomes.map((tome, index) => {
            const Icon = tome.icon;
            return <Reveal key={tome.number} delay={index * 0.04} data-id="element-112">
                  <article className="h-full overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm" data-id="element-113">
                    <div className={`${tome.color} p-5 text-white`} data-id="element-114">
                      <p className="text-[10px] font-black tracking-widest" data-id="element-115">
                        {tome.number}
                      </p>
                      <Icon className="mt-4 h-9 w-9" data-id="element-116" />
                      <h3 className="mt-5 text-xl font-black leading-tight" data-id="element-117">
                        {tome.title}
                      </h3>
                    </div>
                    <div className="p-5" data-id="element-118">
                      <p className="text-sm leading-6 text-slate-600" data-id="element-119">
                        {tome.copy}
                      </p>
                      <p className="mt-5 border-t border-emerald-100 pt-4 text-xs font-bold text-emerald-700" data-id="element-120">
                        📄 PDF · 50 páginas
                      </p>
                    </div>
                  </article>
                </Reveal>;
          })}
          </div>
          <Reveal className="mx-auto mt-8 max-w-3xl" data-id="element-121">
            <article className="rounded-2xl border border-amber-300 bg-amber-50 p-6 sm:flex sm:items-center sm:gap-6" data-id="element-122">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-amber-400 text-2xl" data-id="element-123">
                🎁
              </div>
              <div data-id="element-124">
                <p className="text-xs font-black tracking-widest text-amber-700" data-id="element-125">
                  BONO ESPECIAL · INCLUIDO SIN COSTO EXTRA
                </p>
                <h3 className="mt-1 text-xl font-black" data-id="element-126">
                  50 Actividades Visuales Para Volver a la Calma
                </h3>
                <p className="mt-2 text-sm text-slate-600" data-id="element-127">
                  50 dinámicas breves con imagen demostrativa y frase guía lista
                  para usar. Para recuperar el clima del aula en minutos.
                </p>
                <p className="mt-3 text-xs font-bold text-emerald-700" data-id="element-128">
                  📄 PDF · Acceso inmediato · Incluido sin costo extra
                </p>
              </div>
            </article>
            <div className="mt-7 text-center" data-id="element-129">
              <PurchaseButton data-id="element-130">QUIERO MIS 5 TOMOS + BONO AHORA</PurchaseButton>
            </div>
          </Reveal>
        </section>

        <section className="bg-emerald-950 px-5 py-20 text-white sm:px-8" data-id="element-131">
          <Reveal data-id="element-132">
            <SectionHeading inverted eyebrow="¿CÓMO RECIBO MI MATERIAL?" title="Tres pasos simples. Sin complicaciones." data-id="element-133" />
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3" data-id="element-134">
            {[[CreditCardIcon, 'PASO 1', 'Hacés tu pago', 'Completás la compra de forma segura a través de Mercado Pago desde tu celular o computadora.'], [MailIcon, 'PASO 2', 'Recibís tu email', 'Inmediatamente después del pago recibís los enlaces de descarga de los 5 tomos y el bono.'], [DownloadIcon, 'PASO 3', 'Descargás tus PDFs', 'Los descargás a tu dispositivo y los tenés disponibles para siempre, sin necesitar internet.']].map(([Icon, step, title, copy]) => {
            const StepIcon = Icon as typeof CreditCardIcon;
            return <Reveal key={String(step)} data-id="element-135">
                  <article className="h-full rounded-xl bg-emerald-900 p-6" data-id="element-136">
                    <p className="text-[10px] font-black tracking-widest text-amber-300" data-id="element-137">
                      {String(step)}
                    </p>
                    <StepIcon className="mt-4 h-10 w-10 text-emerald-300" data-id="element-138" />
                    <h3 className="mt-5 text-xl font-black" data-id="element-139">{String(title)}</h3>
                    <p className="mt-3 text-sm leading-6 text-emerald-100/75" data-id="element-140">
                      {String(copy)}
                    </p>
                  </article>
                </Reveal>;
          })}
          </div>
          <p className="mx-auto mt-9 max-w-3xl text-center text-sm font-bold text-emerald-100" data-id="element-141">
            Sin esperas. Sin envíos físicos. Sin complicaciones. Pagás y en
            minutos ya tenés todo descargado en tu dispositivo.
          </p>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-142">
          <Reveal data-id="element-143">
            <SectionHeading eyebrow="¿CÓMO SE PAGA?" title="Pago seguro a través de Mercado Pago." data-id="element-144" />
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-145">
            {[['💳', 'Tarjeta de crédito', 'Todas las tarjetas aceptadas'], ['✅', 'Tarjeta de débito', 'Débito inmediato y seguro'], ['💰', 'Saldo de Mercado Pago', 'Usá tu saldo disponible']].map(([emoji, title, copy]) => <article key={title} className="rounded-xl bg-white p-6 text-center shadow-sm" data-id="element-146">
                <span className="text-3xl" data-id="element-147">{emoji}</span>
                <h3 className="mt-3 font-black" data-id="element-148">{title}</h3>
                <p className="mt-2 text-sm text-slate-600" data-id="element-149">{copy}</p>
              </article>)}
          </div>
          <div className="mx-auto mt-7 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-150">
            {[[LockKeyholeIcon, 'Pago 100% seguro', 'Cada transacción está protegida con tecnología de encriptación avanzada.'], [ZapIcon, 'Procesamiento inmediato', 'Tu pago se procesa en segundos. Sin esperas ni demoras.'], [MailIcon, 'Entrega automática por email', 'Al confirmar el pago recibís tus PDFs directamente en tu correo.']].map(([Icon, title, copy]) => {
            const PaymentIcon = Icon as typeof LockKeyholeIcon;
            return <article key={String(title)} className="rounded-xl border border-emerald-100 bg-white p-6" data-id="element-151">
                  <PaymentIcon className="h-8 w-8 text-emerald-600" data-id="element-152" />
                  <h3 className="mt-3 font-black" data-id="element-153">{String(title)}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-154">
                    {String(copy)}
                  </p>
                </article>;
          })}
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8" data-id="element-155">
          <Reveal data-id="element-156">
            <SectionHeading eyebrow="EL DIFERENCIAL" title="¿Por qué este recurso es diferente a todo lo que conocés?" data-id="element-157" />
          </Reveal>
          <div className="mx-auto mt-10 max-w-5xl" data-id="element-158">
            <div className="space-y-3 md:hidden" data-id="element-159">
              {[['Requieren materiales especiales', 'Usan materiales del aula o ninguno'], ['Hay que preparar con anticipación', 'Se abre, se elige y se empieza'], ['Los juegos son competitivos', 'Todos los juegos son cooperativos'], ['No están organizados por necesidad', 'Índice por tiempo, edad y estado del grupo'], ['Las explicaciones son largas y confusas', 'Cada ficha es clara, breve y paso a paso'], ['No consideran el espacio del aula', 'Todo pensado para realizarse adentro'], ['Hay que esperar un envío físico', '100% digital, descargás al instante']].map(([oldWay, newWay]) => <article key={oldWay} className="overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm" data-id="element-160">
                  <div className="border-b border-rose-100 bg-rose-50 px-4 py-3" data-id="element-161">
                    <p className="text-[10px] font-black uppercase tracking-wider text-rose-600" data-id="element-162">
                      Lo que existe hoy
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-5 text-slate-600" data-id="element-163">
                      ✕ {oldWay}
                    </p>
                  </div>
                  <div className="bg-emerald-50 px-4 py-3" data-id="element-164">
                    <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700" data-id="element-165">
                      ¡Maestra… ¿A qué jugamos?
                    </p>
                    <p className="mt-1 text-sm font-black leading-5 text-emerald-950" data-id="element-166">
                      ✓ {newWay}
                    </p>
                  </div>
                </article>)}
            </div>

            <div className="hidden overflow-hidden rounded-2xl border border-emerald-100 md:block" data-id="element-167">
              <div className="grid grid-cols-2 bg-emerald-950 text-sm font-black text-white" data-id="element-168">
                <div className="p-4 text-white/65" data-id="element-169">LO QUE EXISTE HOY</div>
                <div className="bg-emerald-600 p-4" data-id="element-170">
                  ¡MAESTRA… ¿A QUÉ JUGAMOS?
                </div>
              </div>
              {[['Requieren materiales especiales', 'Usan materiales del aula o ninguno'], ['Hay que preparar con anticipación', 'Se abre, se elige y se empieza'], ['Los juegos son competitivos', 'Todos los juegos son cooperativos'], ['No están organizados por necesidad', 'Índice por tiempo, edad y estado del grupo'], ['Las explicaciones son largas y confusas', 'Cada ficha es clara, breve y paso a paso'], ['No consideran el espacio del aula', 'Todo pensado para realizarse adentro'], ['Hay que esperar un envío físico', '100% digital, descargás al instante']].map(([oldWay, newWay]) => <div className="grid grid-cols-2 border-t border-emerald-100 text-sm" key={oldWay} data-id="element-171">
                  <div className="p-4 text-slate-500" data-id="element-172">❌ {oldWay}</div>
                  <div className="bg-emerald-50 p-4 font-bold text-emerald-900" data-id="element-173">
                    ✅ {newWay}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-174">
          <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-175">
            <Reveal data-id="element-176">
              <div className="rounded-2xl border-4 border-emerald-950 bg-white p-5 shadow-xl" data-id="element-177">
                <p className="text-[10px] font-black tracking-widest text-emerald-600" data-id="element-178">
                  EJEMPLO DE FICHA
                </p>
                <h3 className="mt-3 text-3xl font-black" data-id="element-179">
                  El Director Invisible
                </h3>
                <div className="mt-5 grid grid-cols-2 gap-2 text-sm" data-id="element-180">
                  <span className="rounded-lg bg-emerald-50 p-3" data-id="element-181">
                    <b data-id="element-182">Edad</b>
                    <br data-id="element-183" />5 a 10 años
                  </span>
                  <span className="rounded-lg bg-emerald-50 p-3" data-id="element-184">
                    <b data-id="element-185">Duración</b>
                    <br data-id="element-186" />5 a 10 minutos
                  </span>
                  <span className="rounded-lg bg-emerald-50 p-3" data-id="element-187">
                    <b data-id="element-188">Material</b>
                    <br data-id="element-189" />
                    Ninguno
                  </span>
                  <span className="rounded-lg bg-emerald-50 p-3" data-id="element-190">
                    <b data-id="element-191">Movimiento</b>
                    <br data-id="element-192" />
                    Bajo
                  </span>
                </div>
                <p className="mt-5 text-sm leading-6" data-id="element-193">
                  <b data-id="element-194">Objetivo:</b> Descubrir quién dirige los movimientos del
                  grupo mientras todos cooperan para imitarlo.
                </p>
                <p className="mt-3 text-sm leading-6" data-id="element-195">
                  <b data-id="element-196">Cómo se juega:</b> Paso a paso claro y directo.
                </p>
                <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm" data-id="element-197">
                  💡 <b data-id="element-198">Consejo para la maestra:</b> Tip práctico para que el
                  juego funcione desde el primer intento.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.08} data-id="element-199">
              <p className="eyebrow" data-id="element-200">ASÍ SE VE CADA JUEGO</p>
              <h2 className="mt-3 text-3xl font-black leading-tight" data-id="element-201">
                Todo lo que necesitás en una sola ficha lista para usar.
              </h2>
              <ul className="mt-6 space-y-3 text-sm font-semibold" data-id="element-202">
                {['Fichas listas en PDF de alta calidad.', 'Cada juego con edad recomendada, duración y materiales necesarios.', 'Variantes para adaptar a distintas edades y grupos.', 'Consejo pedagógico al final de cada ficha.', 'Índice rápido para encontrar el juego correcto en segundos.'].map(item => <li key={item} className="flex gap-2" data-id="element-203">
                    <CircleCheckIcon className="h-5 w-5 shrink-0 text-emerald-500" data-id="element-204" />
                    {item}
                  </li>)}
              </ul>
            </Reveal>
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8" data-id="element-205">
          <Reveal data-id="element-206">
            <SectionHeading eyebrow="ÍNDICE POR NECESIDAD" title="Encontrás el juego ideal en segundos." data-id="element-207" />
          </Reveal>
          <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-emerald-100" data-id="element-208">
            <div className="grid grid-cols-[1.3fr_1fr] bg-emerald-950 p-4 text-xs font-black text-white" data-id="element-209">
              <span data-id="element-210">SI EL GRUPO ESTÁ…</span>
              <span data-id="element-211">IR AL TOMO</span>
            </div>
            {needIndex.map(([emoji, need, tome]) => <div className="grid grid-cols-[1.3fr_1fr] border-t border-emerald-100 text-sm" key={need} data-id="element-212">
                <span className="p-4" data-id="element-213">
                  {emoji} {need}
                </span>
                <span className="bg-emerald-50 p-4 font-bold text-emerald-800" data-id="element-214">
                  {tome}
                </span>
              </div>)}
          </div>
        </section>

        <section id="comprar" className="bg-emerald-950 px-5 py-20 text-white sm:px-8" data-id="element-215">
          <Reveal data-id="element-216">
            <SectionHeading inverted eyebrow="UNA SOLA INVERSIÓN" title="Todo el pack por solo $490 pesos uruguayos." data-id="element-217" />
          </Reveal>
          <Reveal delay={0.06} className="mx-auto mt-10 max-w-4xl rounded-3xl bg-white p-6 text-emerald-950 shadow-2xl sm:p-8" data-id="element-218">
            <div className="grid grid-cols-[1.4fr_1fr] border-b border-emerald-100 pb-3 text-xs font-black text-emerald-700" data-id="element-219">
              <span data-id="element-220">QUÉ RECIBÍS</span>
              <span data-id="element-221">FORMATO</span>
            </div>
            {['Tomo 1: 30 Juegos Sin Materiales', 'Tomo 2: 30 Juegos Con Hojas y Lápices', 'Tomo 3: 30 Juegos de Movimiento Dentro del Aula', 'Tomo 4: 30 Juegos Para Volver a la Calma', 'Tomo 5: 30 Juegos Rápidos de 5 Minutos', 'BONO: 50 Actividades Visuales Para Volver a la Calma', 'Índice por necesidad del grupo', 'Acceso inmediato y de por vida', 'Garantía incondicional de 7 días'].map((item, index) => <div className="grid grid-cols-[1.4fr_1fr] border-b border-emerald-50 py-3 text-xs sm:text-sm" key={item} data-id="element-222">
                <span className="font-semibold" data-id="element-223">{item}</span>
                <span className="text-emerald-700" data-id="element-224">
                  {index < 6 ? '📄 PDF · Incluido' : index === 7 ? '⚡ Al instante' : index === 8 ? '🛡️ Sin preguntas' : '📄 Incluido'}
                </span>
              </div>)}
            <div className="pt-8 text-center" data-id="element-225">
              <p className="text-xs font-black tracking-widest text-emerald-600" data-id="element-226">
                TODO ESTO POR SOLO
              </p>
              <p className="mt-2 text-5xl font-black text-emerald-700" data-id="element-227">$490</p>
              <p className="text-lg font-bold" data-id="element-228">pesos uruguayos</p>
              <p className="mt-2 text-sm text-slate-600" data-id="element-229">
                100% PDF | Acceso inmediato | Pago seguro mediante Mercado Pago.
              </p>
              <div className="mt-6" data-id="element-230">
                <PurchaseButton data-id="element-231">
                  QUIERO MI PACK MAESTRA… ¿A QUÉ JUGAMOS? POR $490
                </PurchaseButton>
              </div>
              <p className="mt-5 text-xs leading-6 text-slate-500" data-id="element-232">
                💳 Pago seguro · 📄 Recibís tus 5 PDFs por email al instante ·
                📱 Desde celular, tablet o computadora · 🛡️ Garantía de 7 días
              </p>
            </div>
          </Reveal>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-233">
          <Reveal data-id="element-234">
            <SectionHeading eyebrow="MÁS DE 500 MAESTRAS YA LO USAN" title="Esto es lo que dicen quienes ya tienen el material." data-id="element-235" />
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-236">
            {testimonials.map((testimonial, index) => <Reveal delay={index * 0.04} key={testimonial.name} data-id="element-237">
                <article className="h-full rounded-xl border border-emerald-100 bg-white p-5 shadow-sm" data-id="element-238">
                  <div className="flex gap-1 text-amber-400" data-id="element-239">
                    {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" data-id="element-240" />)}
                  </div>
                  <blockquote className="mt-4 text-sm leading-6 text-slate-700" data-id="element-241">
                    “{testimonial.quote}”
                  </blockquote>
                  <div className="mt-5 border-t border-emerald-100 pt-3" data-id="element-242">
                    <p className="text-sm font-black" data-id="element-243">{testimonial.name}</p>
                    <p className="text-xs text-slate-500" data-id="element-244">{testimonial.role}</p>
                  </div>
                </article>
              </Reveal>)}
          </div>
        </section>

        <section className="bg-amber-50 px-5 py-16 sm:px-8" data-id="element-245">
          <div className="mx-auto max-w-5xl rounded-2xl border border-amber-300 bg-white p-7 text-center shadow-sm" data-id="element-246">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-amber-500" data-id="element-247" />
            <p className="mt-4 text-xs font-black tracking-widest text-amber-700" data-id="element-248">
              CERO RIESGO PARA VOS
            </p>
            <h2 className="mt-2 text-3xl font-black" data-id="element-249">
              Probalo durante 7 días. Sin preguntas.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-250">
              Si dentro de los primeros 7 días después de recibir tus PDFs
              sentís que el material no es lo que esperabas, te devolvemos el
              100% de tu dinero. Sin formularios complicados. Sin demoras.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-251">
              {[['📅', '7 días completos', 'Tenés una semana entera para probarlo con tu grupo.'], ['💰', '100% de tu dinero', 'Si no te convence te devolvemos todo lo que pagaste.'], ['💬', 'Sin preguntas', 'No tenés que explicar ni justificar nada.']].map(([emoji, title, copy]) => <div key={title} data-id="element-252">
                  <span className="text-2xl" data-id="element-253">{emoji}</span>
                  <h3 className="mt-2 font-black" data-id="element-254">{title}</h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-255">{copy}</p>
                </div>)}
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8" data-id="element-256">
          <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[.7fr_1.3fr]" data-id="element-257">
            <Reveal data-id="element-258">
              <div className="mx-auto grid h-44 w-44 place-items-center rounded-full bg-emerald-100 text-center text-5xl" data-id="element-259">
                🏫
              </div>
            </Reveal>
            <Reveal delay={0.08} data-id="element-260">
              <p className="eyebrow" data-id="element-261">¿QUIÉN ESTÁ DETRÁS DE ESTO?</p>
              <h2 className="mt-3 text-3xl font-black" data-id="element-262">
                Somos Aula de Éxitos.
              </h2>
              <p className="mt-4 text-sm leading-6 text-slate-600" data-id="element-263">
                Creamos recursos educativos digitales para maestras que quieren
                hacer su trabajo con más herramientas, menos improvisación y más
                confianza.
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-264">
                Cada juego fue pensado, estructurado y revisado para que
                funcione en el aula real: con grupos reales, espacios reales y
                el tiempo real que tiene una maestra.
              </p>
              <p className="mt-3 text-sm font-semibold text-emerald-900" data-id="element-265">
                No son juegos para competir. Son juegos para escuchar, moverse,
                calmarse, resolver y disfrutar juntos.
              </p>
              <p className="mt-5 text-sm" data-id="element-266">
                Encontrá todos nuestros materiales en{' '}
                <a href="https://www.tufenixdigital.store" className="font-bold text-emerald-700 underline" data-id="element-267">
                  www.tufenixdigital.store
                </a>
              </p>
              <div className="mt-6 grid grid-cols-3 rounded-xl bg-emerald-50 p-4 text-center text-emerald-800" data-id="element-268">
                <div data-id="element-269">
                  <b className="block text-lg" data-id="element-270">150</b>
                  <span className="text-[10px]" data-id="element-271">juegos PDF</span>
                </div>
                <div data-id="element-272">
                  <b className="block text-lg" data-id="element-273">500+</b>
                  <span className="text-[10px]" data-id="element-274">maestras</span>
                </div>
                <div data-id="element-275">
                  <b className="block text-lg" data-id="element-276">4.9★</b>
                  <span className="text-[10px]" data-id="element-277">valoración</span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-emerald-50 px-5 py-20 sm:px-8" data-id="element-278">
          <Reveal data-id="element-279">
            <SectionHeading eyebrow="PREGUNTAS FRECUENTES" title="Todo lo que necesitás saber antes de comprarlo." data-id="element-280" />
          </Reveal>
          <div className="mx-auto mt-9 max-w-4xl space-y-2" data-id="element-281">
            {faqs.map(([question, answer], index) => <article key={question} className="overflow-hidden rounded-xl border border-emerald-100 bg-white" data-id="element-282">
                <button type="button" className="flex w-full items-center justify-between gap-4 p-5 text-left text-sm font-black text-emerald-950" aria-expanded={openFaq === index} onClick={() => setOpenFaq(openFaq === index ? null : index)} data-id="element-283">
                  {question}
                  <ChevronDownIcon className={`h-5 w-5 shrink-0 text-emerald-600 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-284" />
                </button>
                {openFaq === index && <div className="border-t border-emerald-100 px-5 py-4 text-sm leading-6 text-slate-600" data-id="element-285">
                    {answer}
                  </div>}
              </article>)}
          </div>
        </section>

        <section className="bg-emerald-700 px-5 py-20 text-center text-white sm:px-8" data-id="element-286">
          <Reveal data-id="element-287">
            <div className="mx-auto max-w-3xl" data-id="element-288">
              <p className="text-xs font-black tracking-widest text-amber-300" data-id="element-289">
                5 TOMOS EN PDF + BONO ESPECIAL
              </p>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl" data-id="element-290">
                ¿Lista para tener la respuesta cuando el grupo pregunte?
              </h2>
              <p className="mt-4 text-sm leading-6 text-emerald-50/85" data-id="element-291">
                La próxima vez que llueva, el recreo se cancele o el grupo
                necesite un juego, vas a tener todo lo que necesitás. Más de 500
                maestras ya transformaron esos momentos difíciles del aula con
                este material.
              </p>
              <p className="mt-7 text-4xl font-black text-amber-300" data-id="element-292">
                $490{' '}
                <span className="text-base text-white" data-id="element-293">pesos uruguayos</span>
              </p>
              <p className="mt-2 text-sm font-bold text-emerald-100" data-id="element-294">
                Pago único · Sin cuotas · 100% digital · Acceso inmediato
              </p>
              <div className="mt-7" data-id="element-295">
                <PurchaseButton data-id="element-296">
                  ASEGURAR MI PACK COMPLETO POR $490
                </PurchaseButton>
              </div>
              <PaymentNote data-id="element-297" />
            </div>
          </Reveal>
        </section>

        <section className="bg-emerald-950 px-5 py-12 text-center text-emerald-50 sm:px-8" data-id="element-298">
          <blockquote className="mx-auto max-w-3xl text-lg font-bold leading-7" data-id="element-299">
            “No son juegos para competir. Son juegos para escuchar, moverse,
            calmarse y disfrutar juntos. Eso es exactamente lo que hace una
            buena maestra.”
          </blockquote>
          <p className="mt-5 text-sm font-black" data-id="element-300">Aula de Éxitos</p>
          <a href="https://www.tufenixdigital.store" className="mt-1 inline-block text-sm text-amber-300" data-id="element-301">
            www.tufenixdigital.store
          </a>
        </section>

        <a href="#comprar" className="fixed inset-x-3 bottom-3 z-30 flex min-h-14 items-center justify-center gap-2 rounded-full bg-amber-400 px-5 text-center text-sm font-black text-emerald-950 shadow-2xl shadow-emerald-950/30 focus:outline-none focus:ring-4 focus:ring-amber-200 sm:hidden" data-id="element-302">
          QUIERO EL PACK POR $490 <ArrowRightIcon size={18} data-id="element-303" />
        </a>

        <footer className="bg-[#042b22] px-5 py-8 pb-24 text-center text-xs leading-6 text-emerald-100/65 sm:pb-8" data-id="element-304">
          <p data-id="element-305">© 2026 Aula de Éxitos — Todos los derechos reservados.</p>
          <p className="mt-2" data-id="element-306">
            💳 Pago seguro procesado a través de Mercado Pago · 🔒 Transacciones
            100% protegidas
          </p>
          <p data-id="element-307">
            📄 Producto 100% digital en PDF · Sin envíos físicos · Sin impresión
            necesaria
          </p>
          <p className="mt-2" data-id="element-308">
            Para consultas y más recursos visitá www.tufenixdigital.store
          </p>
        </footer>
      </main>
    </>;
}